import React from 'react';
import '../styles/theme.css';
import '../styles/RunMonitor.css';
import BruteHeader from '../components/BruteHeader';
import BruteFooter from '../components/BruteFooter';
import RunMonitorMasthead from '../components/RunMonitorMasthead';
import RunMonitorTaskPanel from '../components/RunMonitorTaskPanel';
import RunMonitorLogTicker from '../components/RunMonitorLogTicker';
/* IMPORTS */


/* mostlyagent: optional spatial allocation; measurements are layout inputs, never QA gates */
function usePageSpatialAllocation(rootRef, surfaces, policy) {
  React.useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !surfaces.length) return undefined;
    let frame = 0;
    const observed = new WeakSet();
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    const attribute = 'data-spatial-region';
    const query = (scope, selector) => {
      try { return [...scope.querySelectorAll(selector)]; } catch (_) { return []; }
    };
    const media = surfaces.flatMap(surface => surface.queries.map(q => matchMedia(q.when)));
    function observe(element) {
      if (resize && !observed.has(element)) { resize.observe(element); observed.add(element); }
    }
    function schedule() {
      if (!frame) frame = requestAnimationFrame(update);
    }
    function update() {
      frame = 0;
      const totals = new Map();
      const width = window.innerWidth;
      const profile = width < policy.tablet_min ? 'mobile' : width < policy.desktop_min ? 'tablet' : 'desktop';
      for (const surface of surfaces) {
        let placement = { ...surface, ...(surface.profiles[profile] || {}) };
        for (const q of surface.queries) if (matchMedia(q.when).matches) placement = { ...placement, ...q };
        const slots = query(root, '[data-spatial-slot="1"][data-spatial-section]')
          .filter(e => e.getAttribute('data-spatial-section') === surface.section);
        for (const name of surface.reserve_for) {
          for (const target of query(root, '[data-spatial-page-region="1"][' + attribute + ']')
              .filter(e => e.getAttribute(attribute) === name)) {
            observe(target);
            const values = totals.get(target) || { 'inline-start': 0, 'inline-end': 0, 'block-start': 0, 'block-end': 0 };
            totals.set(target, values);
            if (placement.participation !== 'persistent') continue;
            const box = target.getBoundingClientRect();
            const rtl = getComputedStyle(target).direction === 'rtl';
            for (const slot of slots) for (const element of query(slot, surface.selector)) {
              observe(element);
              const style = getComputedStyle(element);
              // Sticky and normal flow already consume space. Closed/transient surfaces
              // cannot create a permanent reservation, including under reduced motion.
              if (!['fixed', 'absolute'].includes(style.position) || !element.getClientRects().length
                  || style.visibility === 'hidden' || element.closest('[hidden],[inert],[data-presentation-state="closed"]')) continue;
              const rect = element.getBoundingClientRect();
              let edge = placement.edge;
              // Honor an unambiguous live edge attachment if an approved source's
              // media rules change orientation. This uses logical CSS geometry,
              // never component names or a width threshold.
              const zero = value => Number.parseFloat(value) === 0;
              const start = zero(style.insetInlineStart), end = zero(style.insetInlineEnd);
              const top = zero(style.insetBlockStart), bottom = zero(style.insetBlockEnd);
              if (start && end && top !== bottom) edge = top ? 'block-start' : 'block-end';
              else if (top && bottom && start !== end) edge = start ? 'inline-start' : 'inline-end';
              if (rect.right <= box.left || rect.left >= box.right) continue;
              if (!(style.position === 'fixed' && edge.startsWith('inline'))
                  && (rect.bottom <= box.top || rect.top >= box.bottom)) continue;
              const distance = edge === 'inline-start' ? (rtl ? box.right - rect.left : rect.right - box.left)
                : edge === 'inline-end' ? (rtl ? rect.right - box.left : box.right - rect.left)
                : edge === 'block-start' ? rect.bottom - (style.position === 'fixed' ? Math.max(0, box.top + window.scrollY) : box.top)
                : (style.position === 'fixed' ? window.innerHeight : box.bottom) - rect.top;
              values[edge] = Math.max(values[edge], Math.max(0, Math.min(distance,
                edge.startsWith('inline') ? box.width : box.height)));
            }
          }
        }
      }
      for (const [target, values] of totals) for (const [edge, extent] of Object.entries(values)) {
        const key = '--page-reserved-' + edge;
        const value = Math.ceil(extent * 100) / 100 + 'px';
        if (target.style.getPropertyValue(key) !== value) target.style.setProperty(key, value);
      }
    }
    observe(root);
    const mutations = new MutationObserver(schedule);
    mutations.observe(root, { subtree: true, childList: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'inert', 'data-presentation-state'] });
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    root.addEventListener('transitionend', schedule);
    root.addEventListener('animationend', schedule);
    for (const item of media) item.addEventListener('change', schedule);
    update();
    return () => {
      cancelAnimationFrame(frame);
      resize?.disconnect(); mutations.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule);
      root.removeEventListener('transitionend', schedule);
      root.removeEventListener('animationend', schedule);
      for (const item of media) item.removeEventListener('change', schedule);
    };
  }, [rootRef, surfaces, policy]);
}

const PAGE_SPATIAL_SURFACES = [{"section": "RunMonitorHeaderSlot", "selector": ".brute-header", "participation": "persistent", "edge": "block-start", "profiles": {"mobile": {"participation": "persistent", "edge": "block-start"}, "tablet": {"participation": "persistent", "edge": "block-start"}, "desktop": {"participation": "persistent", "edge": "block-start"}}, "queries": [], "reserve_for": ["monitor-body"]}];
const PAGE_SPATIAL_POLICY = {"version": 1, "tablet_min": 768, "desktop_min": 1024};
function RunMonitorPage() {
  const spatialRoot = React.useRef(null);
  usePageSpatialAllocation(spatialRoot, PAGE_SPATIAL_SURFACES, PAGE_SPATIAL_POLICY);
  return (
    <div className="ru-root" ref={spatialRoot} data-spatial-shell="1">
      <div id="runmonitorheaderslot" className="ru-slot-runmonitorheaderslot" data-spatial-slot="1" data-spatial-section="RunMonitorHeaderSlot" data-spatial-owner="BruteHeader">
        <BruteHeader activePage={"Run Monitor"} />
      </div>
      <div data-spatial-page-region="1" data-spatial-region="monitor-body">
        <div className="ru-layout-1">
          <div id="runmonitormasthead" className="ru-slot-runmonitormasthead" data-spatial-slot="1" data-spatial-section="RunMonitorMasthead">
            <RunMonitorMasthead />
          </div>
          <div id="runmonitorlogticker" className="ru-slot-runmonitorlogticker" data-spatial-slot="1" data-spatial-section="RunMonitorLogTicker">
            <RunMonitorLogTicker />
          </div>
          <div id="runmonitortaskpanel" className="ru-slot-runmonitortaskpanel" data-spatial-slot="1" data-spatial-section="RunMonitorTaskPanel">
            <RunMonitorTaskPanel />
          </div>
        </div>
      </div>
      <div id="runmonitorfooterslot" className="ru-slot-runmonitorfooterslot" data-spatial-slot="1" data-spatial-section="RunMonitorFooterSlot">
        <BruteFooter />
      </div>
    </div>
  );
}

export default RunMonitorPage;